<template>
    <div class="glass-container">
        <div class="location-customers-container">
            <div class="modern-card">
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Customer Locations</h1>
                        <p class="page-subtitle">Geographic distribution and analysis of customers</p>
                    </div>
                    
                    <!-- Date Range Picker and Refresh -->
                    <div class="header-actions">
                        <div class="date-range-picker">
                            <label class="small text-muted mb-1 d-block">DATE RANGE</label>
                            <date-picker
                                v-model="date_range"
                                format="MM-DD-YYYY"
                                valueType="format"
                                class="custom-datepicker"
                                placeholder="SELECT DATE RANGE"
                                range
                                @change="handleDateChange"
                                size="sm"
                            ></date-picker>
                        </div>
                        
                        <b-button 
                            variant="success" 
                            size="sm" 
                            @click="loadCustomerData"
                            :disabled="loadingData"
                            class="search-btn ml-2"
                        >
                            <b-icon-search class="mr-1"></b-icon-search>
                            {{ loadingData ? 'LOADING...' : 'SEARCH' }}
                        </b-button>
                        
                        <b-button 
                            variant="outline-primary" 
                            size="sm" 
                            @click="refreshData"
                            :disabled="loadingData"
                            class="refresh-btn ml-1"
                        >
                            <b-icon-arrow-clockwise></b-icon-arrow-clockwise>
                        </b-button>
                    </div>
                </div>

                <!-- Quick Date Buttons -->
                <div class="quick-date-buttons mb-3">
                    <b-button
                        v-for="btn in quickDateButtons"
                        :key="btn.id"
                        size="sm"
                        :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                        @click="setQuickDateRange(btn.id)"
                        class="quick-date-btn"
                    >
                        {{ btn.label }}
                    </b-button>
                </div>

                <!-- Tabs Navigation -->
                <div class="glass-tabs">
                    <div class="tabs-nav">
                        <div 
                            v-for="tab in tabs" 
                            :key="tab.id"
                            :class="['tab-nav-item', { 'active': activeTab === tab.id }]"
                            @click="activeTab = tab.id"
                        >
                            <b-icon :icon="tab.icon" class="mr-2"></b-icon>
                            {{ tab.title }}
                        </div>
                    </div>

                    <!-- Geographic Map Tab - Use v-show instead of v-if for better performance -->
                    <div v-show="activeTab === 'map'" class="tab-content">
                        <div class="map-section">
                            <!-- Loading state -->
                            <div v-if="loadingData" class="loading-container">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="sr-only">Loading...</span>
                                </div>
                                <div class="loading-text">Loading customer location data...</div>
                                <div class="date-range-info mt-2">
                                    <small class="text-muted">
                                        Date range: {{ formatDisplayDate(date_range[0]) }} to {{ formatDisplayDate(date_range[1]) }}
                                    </small>
                                </div>
                            </div>
                            
                            <!-- Map container -->
                            <div v-else class="map-container-glass">
                                <!-- Map loading overlay -->
                                <div v-if="!mapInitialized && !mapLoadError" class="map-loading-overlay">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="sr-only">Initializing map...</span>
                                    </div>
                                    <div class="loading-text">Loading map visualization...</div>
                                </div>
                                
                                <!-- Map error overlay -->
                                <div v-if="mapLoadError" class="map-error-overlay">
                                    <b-icon-exclamation-triangle class="error-icon"></b-icon-exclamation-triangle>
                                    <div class="error-text">Failed to load map</div>
                                    <b-button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        @click="retryMapLoad"
                                        class="mt-2"
                                    >
                                        <b-icon-arrow-clockwise class="mr-1"></b-icon-arrow-clockwise>
                                        Retry
                                    </b-button>
                                </div>
                                
                                <!-- The actual map - Only render when map tab is active -->
                                <div v-if="activeTab === 'map'" id="customerMap" class="glass-map"></div>
                                
                                <!-- Stats overlay -->
                                <div v-if="mapInitialized && !mapLoadError" class="stats-overlay">
                                    <div class="stats-grid">
                                        <div 
                                            v-for="stat in mapStats" 
                                            :key="stat.label"
                                            class="stat-card-glass"
                                        >
                                            <div class="stat-icon">
                                                <b-icon :icon="stat.icon"></b-icon>
                                            </div>
                                            <div class="stat-content">
                                                <div class="stat-value">{{ stat.value }}</div>
                                                <div class="stat-label">{{ stat.label }}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="date-range-badge mt-2">
                                        <small class="text-muted">
                                            {{ formatDisplayDate(date_range[0]) }} - {{ formatDisplayDate(date_range[1]) }}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- State Breakdown Tab -->
                    <div v-show="activeTab === 'state'" class="tab-content">
                        <StateWiseCustomers :location-data="locationData" :date-range="date_range" />
                    </div>

                    <!-- Zip Code Analysis Tab -->
                    <div v-show="activeTab === 'zip'" class="tab-content">
                        <ZipCodeCustomers :location-data="locationData" :date-range="date_range" />
                    </div>

                    <!-- Location Performance Tab -->
                    <div v-show="activeTab === 'performance'" class="tab-content">
                        <LocationPerformance :location-data="locationData" :date-range="date_range" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import StateWiseCustomers from './StateWiseCustomers.vue';
import ZipCodeCustomers from './ZipCodeCustomers.vue';
import LocationPerformance from './LocationPerformance.vue';
import Requestor from '@/utilities/Requestor';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import { 
    BIconArrowClockwise, 
    BIconSearch,
    BIconGeoAlt,
    BIconPeople,
    BIconCash,
    BIconGraphUp,
    BIconMap,
    BIconFlag,
    BIconPostcard,
    BIconBarChart,
    BIconExclamationTriangle
} from 'bootstrap-vue';

// Fix for Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

export default {
    name: 'LocationCustomers',
    components: {
        StateWiseCustomers,
        ZipCodeCustomers,
        LocationPerformance,
        DatePicker,
        BIconArrowClockwise,
        BIconSearch,
        BIconGeoAlt,
        BIconPeople,
        BIconCash,
        BIconGraphUp,
        BIconMap,
        BIconFlag,
        BIconPostcard,
        BIconBarChart,
        BIconExclamationTriangle
    },
    data() {
        return {
            locationData: [],
            totalCustomers: 0,
            totalRevenue: 0,
            avgOrderValue: 0,
            map: null,
            markers: [],
            loadingData: false,
            mapInitialized: false,
            items_being_loaded: false,
            activeTab: 'map',
            
            // Date Range variables
            date_range: [],
            picked_date_range: 'this_month', // Default to this month
            
            // Quick date buttons
            quickDateButtons: [
                { id: 'today', label: 'Today' },
                { id: 'yesterday', label: 'Yesterday' },
                { id: 'this_week', label: 'This Week' },
                { id: 'last_week', label: 'Last Week' },
                { id: 'this_month', label: 'This Month' },
                { id: 'last_month', label: 'Last Month' },
                { id: 'all_time', label: 'All Time' }
            ],
            
            tabs: [
                { id: 'map', title: 'Geographic Map', icon: 'geo-alt' },
                { id: 'state', title: 'State Breakdown', icon: 'flag' },
                { id: 'zip', title: 'Zip Code Analysis', icon: 'postcard' },
                { id: 'performance', title: 'Location Performance', icon: 'bar-chart' }
            ],
            
            // Map loading state
            mapLoadError: false,
            mapRetryCount: 0,
            maxRetries: 3,
            resizeTimeout: null,
            mapLoadTimeout: null,
            
            // State coordinates for fallback mapping
            stateCoordinates: {
                'AL': { lat: 32.806671, lng: -86.791130, name: 'Alabama' },
                'AK': { lat: 61.370716, lng: -152.404419, name: 'Alaska' },
                'AZ': { lat: 33.729759, lng: -111.431221, name: 'Arizona' },
                'AR': { lat: 34.969704, lng: -92.373123, name: 'Arkansas' },
                'CA': { lat: 36.116203, lng: -119.681564, name: 'California' },
                'CO': { lat: 39.059811, lng: -105.311104, name: 'Colorado' },
                'CT': { lat: 41.597782, lng: -72.755371, name: 'Connecticut' },
                'DE': { lat: 39.318523, lng: -75.507141, name: 'Delaware' },
                'FL': { lat: 27.766279, lng: -81.686783, name: 'Florida' },
                'GA': { lat: 33.040619, lng: -83.643074, name: 'Georgia' },
                'HI': { lat: 21.094318, lng: -157.498337, name: 'Hawaii' },
                'ID': { lat: 44.240459, lng: -114.478828, name: 'Idaho' },
                'IL': { lat: 40.349457, lng: -88.986137, name: 'Illinois' },
                'IN': { lat: 39.849426, lng: -86.258278, name: 'Indiana' },
                'IA': { lat: 42.011539, lng: -93.210526, name: 'Iowa' },
                'KS': { lat: 38.526600, lng: -96.726486, name: 'Kansas' },
                'KY': { lat: 37.668140, lng: -84.670067, name: 'Kentucky' },
                'LA': { lat: 31.169546, lng: -91.867805, name: 'Louisiana' },
                'ME': { lat: 44.693947, lng: -69.381927, name: 'Maine' },
                'MD': { lat: 39.063946, lng: -76.802101, name: 'Maryland' },
                'MA': { lat: 42.230171, lng: -71.530106, name: 'Massachusetts' },
                'MI': { lat: 43.326618, lng: -84.536095, name: 'Michigan' },
                'MN': { lat: 45.694454, lng: -93.900192, name: 'Minnesota' },
                'MS': { lat: 32.741646, lng: -89.678696, name: 'Mississippi' },
                'MO': { lat: 38.456085, lng: -92.288368, name: 'Missouri' },
                'MT': { lat: 46.921925, lng: -110.454353, name: 'Montana' },
                'NE': { lat: 41.125370, lng: -98.268082, name: 'Nebraska' },
                'NV': { lat: 38.313515, lng: -117.055374, name: 'Nevada' },
                'NH': { lat: 43.452492, lng: -71.563896, name: 'New Hampshire' },
                'NJ': { lat: 40.298904, lng: -74.521011, name: 'New Jersey' },
                'NM': { lat: 34.840515, lng: -106.248482, name: 'New Mexico' },
                'NY': { lat: 42.165726, lng: -74.948051, name: 'New York' },
                'NC': { lat: 35.630066, lng: -79.806419, name: 'North Carolina' },
                'ND': { lat: 47.528912, lng: -99.784012, name: 'North Dakota' },
                'OH': { lat: 40.388783, lng: -82.764915, name: 'Ohio' },
                'OK': { lat: 35.565342, lng: -96.928917, name: 'Oklahoma' },
                'OR': { lat: 44.572021, lng: -122.070938, name: 'Oregon' },
                'PA': { lat: 40.590752, lng: -77.209755, name: 'Pennsylvania' },
                'RI': { lat: 41.680893, lng: -71.511780, name: 'Rhode Island' },
                'SC': { lat: 33.856892, lng: -80.945007, name: 'South Carolina' },
                'SD': { lat: 44.299782, lng: -99.438828, name: 'South Dakota' },
                'TN': { lat: 35.747845, lng: -86.692345, name: 'Tennessee' },
                'TX': { lat: 31.054487, lng: -97.563461, name: 'Texas' },
                'UT': { lat: 40.150032, lng: -111.862434, name: 'Utah' },
                'VT': { lat: 44.045876, lng: -72.710686, name: 'Vermont' },
                'VA': { lat: 37.769337, lng: -78.169968, name: 'Virginia' },
                'WA': { lat: 47.400902, lng: -121.490494, name: 'Washington' },
                'WV': { lat: 38.491226, lng: -80.954453, name: 'West Virginia' },
                'WI': { lat: 44.268543, lng: -89.616508, name: 'Wisconsin' },
                'WY': { lat: 42.755966, lng: -107.302490, name: 'Wyoming' }
            }
        };
    },
    computed: {
        mapStats() {
            return [
                {
                    label: 'Locations',
                    value: this.locationData.length,
                    icon: 'geo-alt'
                },
                {
                    label: 'Customers',
                    value: this.totalCustomers.toLocaleString(),
                    icon: 'people'
                },
                {
                    label: 'Revenue',
                    value: `$${this.totalRevenue.toLocaleString()}`,
                    icon: 'cash'
                },
                {
                    label: 'Avg Order',
                    value: `$${this.avgOrderValue.toFixed(2)}`,
                    icon: 'graph-up'
                }
            ];
        }
    },
    mounted() {
        // Initialize date range
        this.initializeDateRange();
        
        // Load customer data
        this.loadCustomerData();
        
        // Add resize listener
        window.addEventListener('resize', this.debouncedResizeMap);
    },
    beforeDestroy() {
        // Clean up resize listener
        window.removeEventListener('resize', this.debouncedResizeMap);
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        if (this.mapLoadTimeout) {
            clearTimeout(this.mapLoadTimeout);
        }
        
        // Clean up map
        this.cleanupMap();
    },
    methods: {
        initializeDateRange() {
            // Set default date range to last 30 days
            const today = moment();
            const lastMonth = moment().subtract(30, 'days');
            this.date_range = [
                lastMonth.format('MM-DD-YYYY'),
                today.format('MM-DD-YYYY')
            ];
            this.picked_date_range = 'this_month';
        },
        
        setQuickDateRange(value) {
            let start = null;
            let end = null;
            
            switch (value) {
                case 'today':
                    start = moment().format('MM-DD-YYYY');
                    end = moment().format('MM-DD-YYYY');
                    break;
                case 'yesterday':
                    start = moment().subtract(1, 'days').format('MM-DD-YYYY');
                    end = moment().subtract(1, 'days').format('MM-DD-YYYY');
                    break;
                case 'this_week':
                    start = moment().startOf('week').format('MM-DD-YYYY');
                    end = moment().endOf('week').format('MM-DD-YYYY');
                    break;
                case 'last_week':
                    start = moment().subtract(1, 'week').startOf('week').format('MM-DD-YYYY');
                    end = moment().subtract(1, 'week').endOf('week').format('MM-DD-YYYY');
                    break;
                case 'this_month':
                    start = moment().startOf('month').format('MM-DD-YYYY');
                    end = moment().endOf('month').format('MM-DD-YYYY');
                    break;
                case 'last_month':
                    start = moment().subtract(1, 'month').startOf('month').format('MM-DD-YYYY');
                    end = moment().subtract(1, 'month').endOf('month').format('MM-DD-YYYY');
                    break;
                case 'all_time':
                    // Set to a very old date and today
                    start = '2000-01-01';
                    end = moment().format('MM-DD-YYYY');
                    break;
            }
            
            if (start && end) {
                this.date_range = [start, end];
                this.picked_date_range = value;
                this.loadCustomerData();
            }
        },
        
        handleDateChange() {
            // When user manually selects a date range
            this.picked_date_range = null;
        },
        
        loadCustomerData() {
            try {
                this.loadingData = true;
                this.items_being_loaded = true;
                
                // Prepare data with date range
                const data = {};
                if (this.date_range && this.date_range.length === 2) {
                    // Convert dates from MM-DD-YYYY to YYYY-MM-DD format for backend
                    data.start_date = this.convertDateForBackend(this.date_range[0]);
                    data.end_date = this.convertDateForBackend(this.date_range[1]);
                    
                    console.log("Sending dates to location breakdown backend:", {
                        display: this.date_range,
                        backend: data
                    });
                }
                
                // Use Requestor.postSyncP() which handles authentication automatically
                const path = '/api/customers/location_breakdown/';
                
                const success = (response) => {
                    if (response.data.error) {
                        this.$bvToast.toast(
                            response.data.error,
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    } else {
                        // Add coordinates to data (since your API returns null lat/lng)
                        this.locationData = this.addCoordinates(response.data.result);
                        this.calculateSummary();
                        
                        // Update markers on map if map is active
                        if (this.activeTab === 'map' && this.mapInitialized && this.map) {
                            this.plotMapMarkers();
                        }
                        
                        // Show success message
                        this.$bvToast.toast(
                            `Loaded ${this.locationData.length} locations for selected date range`,
                            { title: "Success", autoHideDelay: 1500, variant: "success" }
                        );
                    }
                    this.loadingData = false;
                    this.items_being_loaded = false;
                };
                
                const failure = (error) => {
                    console.error('Error loading location data:', error);
                    
                    // Handle authentication errors
                    if (error.response && error.response.status === 401) {
                        this.$bvToast.toast(
                            "Your session has expired. Please login again.",
                            { title: "Authentication Error", autoHideDelay: 2000, variant: "danger" }
                        );
                        
                        // Redirect to login page
                        setTimeout(() => {
                            // Clear any stored tokens
                            localStorage.removeItem('jwtToken');
                            localStorage.removeItem('token');
                            sessionStorage.removeItem('jwtToken');
                            sessionStorage.removeItem('token');
                            
                            // Redirect to login
                            this.$router.push('/login');
                        }, 2000);
                    } else {
                        this.$bvToast.toast(
                            "Error loading location data. Please refresh.",
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    }
                    
                    this.loadingData = false;
                    this.items_being_loaded = false;
                };
                
                // Use Requestor with proper error handling
                Requestor.postSyncP(path, data, this, success, failure);
                
            } catch (error) {
                console.error('Error in loadCustomerData:', error);
                this.loadingData = false;
                this.items_being_loaded = false;
                this.$bvToast.toast(
                    "Unexpected error occurred. Please try again.",
                    { title: "Error", autoHideDelay: 1500, variant: "warning" }
                );
            }
        },
        
        // Helper to convert date format for backend
        convertDateForBackend(dateStr) {
            if (!dateStr) return dateStr;
            try {
                // Input format: MM-DD-YYYY
                // Output format: YYYY-MM-DD
                const parts = dateStr.split('-');
                if (parts.length === 3) {
                    const month = parts[0];
                    const day = parts[1];
                    const year = parts[2];
                    
                    // Validate month and day
                    if (parseInt(month) >= 1 && parseInt(month) <= 12 && 
                        parseInt(day) >= 1 && parseInt(day) <= 31) {
                        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                    }
                }
                // If format is already YYYY-MM-DD or other, return as-is
                return dateStr;
            } catch (error) {
                console.error('Error converting date format:', error);
                return dateStr;
            }
        },
        
        formatDisplayDate(dateStr) {
            if (!dateStr) return '';
            try {
                return moment(dateStr, 'MM-DD-YYYY').format('MMM D, YYYY');
            } catch (error) {
                return dateStr;
            }
        },
        
        // Add coordinates to data since your API returns null
        addCoordinates(data) {
            return data.map(item => {
                // If coordinates exist, use them
                if (item.latitude && item.longitude) {
                    return item;
                }
                
                // Otherwise, generate coordinates based on state
                let lat, lng;
                
                if (item.state && this.stateCoordinates[item.state]) {
                    const state = this.stateCoordinates[item.state];
                    // Add randomness to avoid markers stacking on same spot
                    lat = state.lat + (Math.random() * 2 - 1) * 0.5;
                    lng = state.lng + (Math.random() * 2 - 1) * 0.5;
                } else {
                    // Fallback to US center with randomness
                    lat = 39.8283 + (Math.random() * 2 - 1) * 5;
                    lng = -98.5795 + (Math.random() * 2 - 1) * 5;
                }
                
                return {
                    ...item,
                    latitude: lat,
                    longitude: lng
                };
            });
        },
        
        calculateSummary() {
            this.totalCustomers = this.locationData.reduce((sum, item) => 
                sum + (item.customer_count || 0), 0);
            this.totalRevenue = this.locationData.reduce((sum, item) => 
                sum + (item.total_revenue || 0), 0);
            
            // Calculate average order value
            const locationsWithOrders = this.locationData.filter(
                item => item.avg_order_value && item.avg_order_value > 0
            );
            
            if (locationsWithOrders.length > 0) {
                const totalAvg = locationsWithOrders.reduce((sum, item) => 
                    sum + item.avg_order_value, 0);
                this.avgOrderValue = totalAvg / locationsWithOrders.length;
            } else {
                this.avgOrderValue = 0;
            }
        },
        
        initMap() {
            // Reset error state
            this.mapLoadError = false;
            
            // Clear any existing timeout
            if (this.mapLoadTimeout) {
                clearTimeout(this.mapLoadTimeout);
            }
            
            // Set a timeout to ensure DOM is ready
            this.mapLoadTimeout = setTimeout(() => {
                this.$nextTick(() => {
                    const mapElement = document.getElementById('customerMap');
                    
                    if (!mapElement) {
                        console.warn('Map element not found, will retry...');
                        // Retry after a short delay
                        setTimeout(() => {
                            if (this.activeTab === 'map') {
                                this.initMap();
                            }
                        }, 300);
                        return;
                    }
                    
                    // Check if map is already initialized
                    if (this.mapInitialized && this.map && this.map._container) {
                        console.log('Map already initialized, invalidating size');
                        this.map.invalidateSize();
                        return;
                    }
                    
                    // Clean up any existing map
                    if (this.map) {
                        try {
                            this.map.remove();
                        } catch (e) {
                            console.warn('Error removing old map:', e);
                        }
                        this.map = null;
                    }
                    
                    try {
                        console.log('Initializing map...');
                        
                        // Initialize map with OpenStreetMap
                        this.map = L.map('customerMap', {
                            preferCanvas: true, // Better performance for many markers
                            zoomControl: true,
                            attributionControl: false
                        }).setView([39.8283, -98.5795], 4);
                        
                        // Add OpenStreetMap tile layer (FREE, no API key)
                        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                            maxZoom: 19,
                            detectRetina: true
                        }).addTo(this.map);
                        
                        // Add scale control
                        L.control.scale({ imperial: true, metric: true }).addTo(this.map);
                        
                        this.mapInitialized = true;
                        this.mapRetryCount = 0; // Reset retry count on success
                        
                        console.log('Map initialized successfully');
                        
                        // Force resize after map is initialized
                        setTimeout(() => {
                            if (this.map) {
                                this.map.invalidateSize();
                            }
                        }, 100);
                        
                        // Plot markers if data is loaded
                        if (this.locationData.length > 0) {
                            setTimeout(() => {
                                this.plotMapMarkers();
                            }, 200);
                        }
                        
                    } catch (error) {
                        console.error('Error initializing map:', error);
                        this.mapLoadError = true;
                        this.mapInitialized = false;
                        
                        // Retry if we haven't exceeded max retries
                        if (this.mapRetryCount < this.maxRetries) {
                            this.mapRetryCount++;
                            console.log(`Retrying map initialization (attempt ${this.mapRetryCount})...`);
                            
                            setTimeout(() => {
                                if (this.activeTab === 'map') {
                                    this.initMap();
                                }
                            }, 1000 * this.mapRetryCount);
                        } else {
                            this.$bvToast.toast('Failed to load map after multiple attempts', {
                                title: 'Map Error',
                                variant: 'danger',
                                solid: true
                            });
                        }
                    }
                });
            }, 150); // Small delay to ensure DOM is fully rendered
        },
        
        plotMapMarkers() {
            if (!this.map || !this.mapInitialized || this.mapLoadError) {
                console.warn('Map not ready for plotting markers, will retry...');
                setTimeout(() => {
                    if (this.mapInitialized && this.locationData.length > 0) {
                        this.plotMapMarkers();
                    }
                }, 500);
                return;
            }
            
            // Ensure map is properly sized
            this.$nextTick(() => {
                setTimeout(() => {
                    if (this.map) {
                        this.map.invalidateSize();
                    }
                    this.actuallyPlotMarkers();
                }, 200);
            });
        },
        
        actuallyPlotMarkers() {
            // Clear existing markers
            this.clearMarkers();
            
            if (this.locationData.length === 0) {
                console.warn('No location data to plot');
                return;
            }
            
            const bounds = L.latLngBounds([]);
            let hasValidMarkers = false;
            
            // Create markers for each location
            this.locationData.forEach((location, index) => {
                const lat = parseFloat(location.latitude);
                const lng = parseFloat(location.longitude);
                
                if (isNaN(lat) || isNaN(lng)) {
                    console.warn('Invalid coordinates for location:', location.location_id);
                    return;
                }
                
                // Create custom marker with glass effect
                const markerColor = this.getMarkerColor(location.customer_count || 0);
                const markerSize = Math.min(30, 15 + Math.sqrt(location.customer_count || 1) * 2);
                
                const customIcon = L.divIcon({
                    html: `
                        <div class="glass-marker" style="
                            background: ${markerColor};
                            backdrop-filter: blur(10px);
                            width: ${markerSize}px;
                            height: ${markerSize}px;
                            border-radius: 50%;
                            border: 2px solid rgba(255, 255, 255, 0.8);
                            box-shadow: 
                                0 4px 15px rgba(0, 0, 0, 0.1),
                                inset 0 0 0 1px rgba(255, 255, 255, 0.3);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: white;
                            font-weight: bold;
                            font-size: ${Math.max(10, markerSize/2)}px;
                            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
                            cursor: pointer;
                            transition: all 0.3s ease;
                        ">
                            ${location.customer_count || 0}
                        </div>
                    `,
                    className: 'custom-glass-marker',
                    iconSize: [markerSize, markerSize],
                    iconAnchor: [markerSize/2, markerSize/2],
                    popupAnchor: [0, -markerSize/2]
                });
                
                // Create marker
                const marker = L.marker([lat, lng], { 
                    icon: customIcon,
                    title: `${location.city || 'Location'} ${location.location_id}`
                }).addTo(this.map);
                
                // Add popup with glass effect
                marker.bindPopup(this.createPopupContent(location));
                
                // Extend bounds
                bounds.extend([lat, lng]);
                hasValidMarkers = true;
                
                // Store marker reference
                this.markers.push(marker);
            });
            
            // Fit bounds if we have valid markers
            if (hasValidMarkers && this.markers.length > 0) {
                setTimeout(() => {
                    if (this.map) {
                        if (this.markers.length === 1) {
                            // Center on single marker with zoom
                            const marker = this.markers[0];
                            this.map.setView(marker.getLatLng(), 10);
                        } else {
                            // Fit bounds for multiple markers
                            this.map.fitBounds(bounds, { 
                                padding: [50, 50],
                                maxZoom: 10
                            });
                        }
                    }
                }, 300);
            }
        },
        
        getMarkerColor(customerCount) {
            if (customerCount > 100) return 'rgba(234, 67, 53, 0.8)'; // Red
            if (customerCount > 50) return 'rgba(251, 188, 5, 0.8)'; // Yellow
            if (customerCount > 20) return 'rgba(52, 168, 83, 0.8)'; // Green
            if (customerCount > 5) return 'rgba(66, 133, 244, 0.8)'; // Blue
            return 'rgba(154, 160, 166, 0.8)'; // Gray
        },
        
        createPopupContent(location) {
            return `
                <div class="glass-popup" style="
                    min-width: 250px; 
                    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
                    background: rgba(255, 255, 255, 0.9);
                    backdrop-filter: blur(10px);
                    border-radius: 12px;
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                ">
                    <div style="
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        padding: 15px;
                        color: white;
                        font-weight: 600;
                        font-size: 14px;
                    ">
                        ${location.city || 'Location'} ${location.location_id}
                        ${location.state ? `, ${location.state}` : ''}
                    </div>
                    <div style="padding: 15px;">
                        <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
                            <span style="color: #4a5568; font-size: 13px;">Customers:</span> 
                            <span style="font-weight: 700; color: #2d3748; background: rgba(66, 153, 225, 0.1); padding: 4px 10px; border-radius: 20px;">${location.customer_count || 0}</span>
                        </div>
                        <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
                            <span style="color: #4a5568; font-size: 13px;">Revenue:</span> 
                            <span style="font-weight: 700; color: #276749;">$${(location.total_revenue || 0).toLocaleString()}</span>
                        </div>
                        <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
                            <span style="color: #4a5568; font-size: 13px;">Avg Order:</span> 
                            <span style="font-weight: 700; color: #c05621;">$${(location.avg_order_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
                            <span style="color: #4a5568; font-size: 13px;">Orders:</span> 
                            <span style="font-weight: 700; color: #6b46c1; background: rgba(159, 122, 234, 0.1); padding: 4px 10px; border-radius: 20px;">${location.total_orders || 0}</span>
                        </div>
                        ${location.customer_zipcodes && location.customer_zipcodes.length > 0 ? `
                        <div style="
                            margin-top: 15px; 
                            padding-top: 12px; 
                            border-top: 1px solid rgba(226, 232, 240, 0.5);
                        ">
                            <div style="color: #718096; font-size: 12px; font-weight: 600; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">
                                Zip Codes
                            </div>
                            <div style="font-size: 11px; color: #4a5568; word-wrap: break-word; line-height: 1.4;">
                                ${Array.isArray(location.customer_zipcodes) ? 
                                    location.customer_zipcodes.slice(0, 5).join(', ') : 
                                    location.customer_zipcodes}
                                ${location.customer_zipcodes.length > 5 ? '...' : ''}
                            </div>
                        </div>` : ''}
                    </div>
                </div>
            `;
        },
        
        clearMarkers() {
            this.markers.forEach(marker => {
                if (marker && marker.remove) {
                    marker.remove();
                }
            });
            this.markers = [];
        },
        
        cleanupMap() {
            this.clearMarkers();
            
            if (this.map) {
                try {
                    this.map.remove();
                } catch (error) {
                    console.warn('Error removing map:', error);
                }
                this.map = null;
            }
            
            this.mapInitialized = false;
        },
        
        debouncedResizeMap() {
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
            
            this.resizeTimeout = setTimeout(() => {
                if (this.map && this.mapInitialized) {
                    this.map.invalidateSize();
                    
                    // Re-plot markers to ensure they're positioned correctly
                    if (this.locationData.length > 0 && this.activeTab === 'map') {
                        setTimeout(() => {
                            this.plotMapMarkers();
                        }, 200);
                    }
                }
            }, 250);
        },
        
        retryMapLoad() {
            this.mapLoadError = false;
            this.mapRetryCount = 0;
            this.cleanupMap();
            this.initMap();
        },
        
        refreshData() {
            this.loadCustomerData();
        }
    },
    watch: {
        locationData: {
            handler(newData) {
                if (newData.length > 0 && this.mapInitialized && this.activeTab === 'map') {
                    // Update markers when data changes
                    setTimeout(() => {
                        this.plotMapMarkers();
                    }, 300);
                }
            },
            deep: true
        },
        activeTab(newTab) {
            console.log('Tab changed to:', newTab);
            if (newTab === 'map') {
                // Wait for Vue to render the tab content
                this.$nextTick(() => {
                    setTimeout(() => {
                        if (!this.mapInitialized && !this.mapLoadError) {
                            this.initMap();
                        } else if (this.map && this.mapInitialized) {
                            // Map exists, just resize it
                            setTimeout(() => {
                                this.map.invalidateSize();
                                if (this.locationData.length > 0) {
                                    this.plotMapMarkers();
                                }
                            }, 100);
                        }
                    }, 300);
                });
            } else if (this.map) {
                // Don't cleanup the map, just hide it
                // This prevents the need to reinitialize when switching back
            }
        }
    }
}
</script>

<style scoped>
.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    z-index: 0;
}

.location-customers-container {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    border-radius: 34px;
}

.modern-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border-radius: 24px;
    box-shadow: 
        0 8px 32px rgba(31, 38, 135, 0.2),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    padding: 30px;
    margin-top: 20px;
    animation: cardAppear 0.6s ease-out;
    position: relative;
    overflow: hidden;
}

.modern-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.8) 50%, 
        transparent 100%);
}

/* Header Section */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
    position: relative;
    flex-wrap: wrap;
    gap: 20px;
}

.header-section::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #364050, #364050);
    border-radius: 2px;
    animation: slideIn 0.6s ease-out;
}

.title-section {
    animation: slideInUp 0.5s ease-out;
    flex: 1;
    min-width: 300px;
}

.page-title {
    font-size: 2.2rem;
    font-weight: 700;
    background: linear-gradient(135deg, #364050 0%, #364050 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 8px;
    text-shadow: 0 2px 10px rgba(102, 126, 234, 0.1);
}

.page-subtitle {
    color: rgba(108, 117, 125, 0.9);
    font-size: 1rem;
    font-weight: 400;
}

.header-actions {
    animation: slideInDown 0.5s ease-out;
    display: flex;
    align-items: flex-end;
    gap: 15px;
    flex-wrap: wrap;
}

.date-range-picker {
    min-width: 300px;
}

.custom-datepicker {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 6px 12px;
    width: 100%;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(5px);
}

.search-btn {
    min-width: 120px;
    height: 38px;
}

.refresh-btn {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(102, 126, 234, 0.3);
    color: #667eea;
    border-radius: 10px;
    padding: 8px 12px;
    font-weight: 500;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    height: 38px;
}

.refresh-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
        transparent, 
        rgba(255, 255, 255, 0.3), 
        transparent);
    transition: left 0.6s ease;
}

.refresh-btn:hover::before {
    left: 100%;
}

.refresh-btn:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

.refresh-btn:disabled {
    opacity: 0.6;
    transform: none !important;
    box-shadow: none !important;
}

/* Quick Date Buttons */
.quick-date-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    padding: 10px;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    border: 1px solid rgba(226, 232, 240, 0.5);
}

.quick-date-btn {
    min-width: 90px;
}

.date-range-info {
    color: #718096;
    font-size: 0.85rem;
    text-align: center;
}

.date-range-badge {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 20px;
    padding: 4px 12px;
    display: inline-block;
    margin-top: 10px;
}

/* Glass Tabs */
.glass-tabs {
    margin-top: 20px;
}

.tabs-nav {
    display: flex;
    gap: 10px;
    margin-bottom: 25px;
    flex-wrap: wrap;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    padding: 10px;
    border-radius: 12px;
    border: 1px solid rgba(226, 232, 240, 0.5);
}

.tab-nav-item {
    display: flex;
    align-items: center;
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 10px;
    color: #4a5568;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    user-select: none;
    position: relative;
    overflow: hidden;
}

.tab-nav-item::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(102, 126, 234, 0.1);
    transform: translate(-50%, -50%);
    transition: width 0.3s ease, height 0.3s ease;
}

.tab-nav-item:hover::before {
    width: 100%;
    height: 100%;
}

.tab-nav-item:hover {
    background: rgba(102, 126, 234, 0.1);
    border-color: #667eea;
    color: #667eea;
    transform: translateY(-2px);
}

.tab-nav-item.active {
    background: linear-gradient(135deg, #364050 0%, #364050 100%);
    color: white;
    border-color: transparent;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.tab-content {
    animation: fadeIn 0.4s ease-out;
}

/* Map Section */
.map-section {
    position: relative;
}

.map-container-glass {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.1),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    min-height: 500px;
}

.glass-map {
    height: 500px;
    width: 100%;
    border-radius: 16px;
    z-index: 1;
}

/* Map loading overlay */
.map-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 2;
    border-radius: 16px;
    animation: fadeIn 0.3s ease;
}

.map-error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 3;
    border-radius: 16px;
    animation: fadeIn 0.3s ease;
    padding: 20px;
    text-align: center;
}

.error-icon {
    font-size: 3rem;
    color: #e53e3e;
    margin-bottom: 15px;
}

.error-text {
    color: #4a5568;
    font-size: 1.1rem;
    margin-bottom: 10px;
    font-weight: 500;
}

.stats-overlay {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 1000;
    min-width: 300px;
    animation: slideInLeft 0.5s ease-out;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
}

.stat-card-glass {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 15px;
    display: flex;
    align-items: center;
    gap: 15px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 
        0 4px 15px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.4);
    transition: all 0.3s ease;
    animation: cardAppear 0.6s ease-out;
    position: relative;
    overflow: hidden;
}

.stat-card-glass::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
        transparent, 
        rgba(255, 255, 255, 0.2), 
        transparent);
    transition: left 0.6s ease;
}

.stat-card-glass:hover::before {
    left: 100%;
}

.stat-card-glass:hover {
    transform: translateY(-3px);
    box-shadow: 
        0 8px 25px rgba(0, 0, 0, 0.1),
        inset 0 0 0 1px rgba(255, 255, 255, 0.5);
}

.stat-icon {
    background: linear-gradient(135deg, #364050 0%,#364050 100%);
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    flex-shrink: 0;
}

.stat-content {
    flex: 1;
}

.stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 4px;
}

.stat-label {
    font-size: 0.85rem;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Loading container */
.loading-container {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 60px 40px;
    text-align: center;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    animation: pulse 2s infinite;
    min-height: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.loading-text {
    color: #4a5568;
    margin-top: 15px;
    font-weight: 500;
}

/* Animation Keyframes */
@keyframes cardAppear {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInLeft {
    from {
        opacity: 0;
        transform: translateX(30px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        width: 0;
    }
    to {
        width: 60px;
    }
}

@keyframes pulse {
    0%, 100% {
        box-shadow: 
            0 4px 20px rgba(0, 0, 0, 0.05),
            inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    }
    50% {
        box-shadow: 
            0 4px 20px rgba(102, 126, 234, 0.15),
            inset 0 0 0 1px rgba(102, 126, 234, 0.1);
    }
}

/* Leaflet overrides */
:deep(.leaflet-control-container .leaflet-control) {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

:deep(.leaflet-control-zoom a) {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    color: #4a5568;
    transition: all 0.3s ease;
}

:deep(.leaflet-control-zoom a:hover) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
}

:deep(.leaflet-popup-content-wrapper) {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
}

:deep(.leaflet-popup-tip) {
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(10px) !important;
    border: 1px solid rgba(226, 232, 240, 0.5) !important;
}

/* Custom marker animations */
:deep(.custom-glass-marker) {
    background: transparent !important;
    border: none !important;
    animation: dropIn 0.5s ease;
    cursor: pointer;
}

:deep(.custom-glass-marker:hover) {
    animation: pulseMarker 1.5s infinite;
    z-index: 1000 !important;
}

@keyframes dropIn {
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.8);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes pulseMarker {
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .glass-container {
        padding: 10px;
    }
    
    .modern-card {
        padding: 20px;
        border-radius: 20px;
        margin-top: 10px;
    }
    
    .page-title {
        font-size: 1.8rem;
    }
    
    .header-section {
        flex-direction: column;
        gap: 15px;
    }
    
    .header-actions {
        width: 100%;
    }
    
    .date-range-picker {
        min-width: 100%;
    }
    
    .stats-overlay {
        position: relative;
        top: auto;
        right: auto;
        margin-top: 20px;
        min-width: 100%;
    }
    
    .stats-grid {
        grid-template-columns: 1fr;
    }
    
    .tabs-nav {
        flex-direction: column;
    }
    
    .tab-nav-item {
        width: 100%;
        justify-content: center;
    }
    
    .glass-map {
        height: 400px;
    }
    
    .map-container-glass {
        min-height: 400px;
    }
    
    .loading-container {
        padding: 40px 20px;
        min-height: 400px;
    }
    
    .quick-date-buttons {
        justify-content: center;
    }
    
    .quick-date-btn {
        min-width: 70px;
        font-size: 11px;
    }
    
    .search-btn {
        min-width: 100px;
    }
}

/* Scrollbar Styling */
::-webkit-scrollbar {
    width: 10px;
}

::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 5px;
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}
</style>


// WEBPACK FOOTER //
// src/components/Customers/LocationCustomers.vue